(() => {
  const STORAGE_KEY = 'soundhousCompareV1';
  const FAMILY = 'portable_home_speaker';
  const FAMILY_TITLE = 'Compare Portable Home Speakers';
  const DEFAULT_TITLE = 'Compare products';

  const SCHEMA = [
    {
      label: 'Which is right for you?',
      rows: [
        ['decision.best_for', 'Best for'],
        ['decision.room_fit', 'Room fit'],
        ['decision.listening_profile', 'Listening profile'],
        ['decision.home_theatre_surround', 'Home theatre surround use'],
        ['decision.stereo_pairing', 'Stereo pairing'],
        ['decision.wet_humid_use', 'Wet & humid-space use'],
        ['decision.outdoor_use', 'Outdoor use'],
        ['decision.key_advantage', 'Key advantage'],
        ['decision.key_compromise', 'Key compromise'],
        ['decision.soundhous_recommendation', 'Soundhous recommendation']
      ]
    },
    {
      label: 'Experience',
      rows: [
        ['experience.battery_life', 'Battery life'],
        ['experience.weather_rating', 'Weather protection'],
        ['experience.bluetooth_audio', 'Bluetooth audio'],
        ['experience.offline_bluetooth_grouping', 'Offline Bluetooth grouping'],
        ['experience.voice_control', 'Voice control'],
        ['experience.trueplay', 'Trueplay'],
        ['experience.line_in', 'Line-in'],
        ['experience.airplay_2', 'Apple AirPlay 2']
      ]
    },
    {
      label: 'Acoustic architecture',
      rows: [
        ['acoustics.amplifiers', 'Amplifiers'],
        ['acoustics.tweeters', 'Tweeters'],
        ['acoustics.woofers', 'Woofers'],
        ['acoustics.architecture', 'Architecture']
      ]
    },
    {
      label: 'Connectivity',
      rows: [
        ['connectivity.wifi', 'WiFi'],
        ['connectivity.bluetooth', 'Bluetooth'],
        ['connectivity.ethernet', 'Ethernet']
      ]
    },
    {
      label: 'Physical',
      rows: [
        ['physical.width', 'Width'],
        ['physical.height', 'Height'],
        ['physical.depth', 'Depth'],
        ['physical.weight', 'Weight'],
        ['physical.colours', 'Colours'],
        ['physical.charging', 'Charging'],
        ['physical.power_source', 'Power source']
      ]
    }
  ];

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const getPath = (object, path) => path.split('.').reduce((value, key) => (
    value && Object.prototype.hasOwnProperty.call(value, key) ? value[key] : null
  ), object);

  const OFFLINE_BT_GROUP_HANDLES = new Set(['sonos-move-2', 'sonos-play']);

  const getValue = (product, path) => {
    const stored = getPath(product?.profile, path);
    if (stored !== null && stored !== undefined && String(stored).trim() !== '') return stored;

    if (path === 'experience.offline_bluetooth_grouping') {
      if (OFFLINE_BT_GROUP_HANDLES.has(product?.handle)) {
        return 'Yes — up to 4 Move 2 / Sonos Play speakers';
      }
      if (product?.handle === 'sonos-roam-2') return 'No';
    }
    return null;
  };

  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

  const loadSelection = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle && item.profile) : [];
    } catch (error) {
      return [];
    }
  };

  const portableSelection = () => loadSelection().filter((item) => item?.profile?.family === FAMILY);

  const cleanupRows = (tbody) => {
    tbody?.querySelectorAll('[data-shcmp-portable-row="true"]').forEach((row) => row.remove());
  };

  const renderPortableRows = (root) => {
    if (!root) return;

    const selection = portableSelection();
    const title = root.querySelector('[id^="SoundhousCompareTitle-"]');
    const modal = root.querySelector('[data-shcmp-modal]');
    const tableWrap = root.querySelector('[data-shcmp-table]');
    const tbody = tableWrap?.querySelector('.shcmp-table tbody');

    if (title) {
      title.textContent = selection.length && selection.every((item) => item?.profile?.family === FAMILY)
        ? FAMILY_TITLE
        : DEFAULT_TITLE;
    }

    if (!tbody) return;

    cleanupRows(tbody);

    if (!modal || modal.hidden || selection.length < 2) return;

    // Future-proofing: if the core Compare engine gains native portable rows,
    // do not inject a second portable table body.
    const existingCoreRows = [...tbody.querySelectorAll('.shcmp-data-row')]
      .filter((row) => row.dataset.shcmpPortableRow !== 'true');
    if (existingCoreRows.length > 0) return;

    const onlyDifferences = Boolean(root.querySelector('[data-shcmp-differences]')?.checked);

    const markup = SCHEMA.map((group, groupIndex) => {
      const rowMarkup = group.rows.map(([path, label]) => {
        const values = selection.map((product) => getValue(product, path));
        const normalized = values.map(normalize).filter(Boolean);
        const unique = new Set(normalized);
        const hasAnyValue = normalized.length > 0;
        const isDifferent = unique.size > 1 || normalized.length !== values.length;

        if (!hasAnyValue || (onlyDifferences && !isDifferent)) return '';

        const rowClasses = [
          'shcmp-data-row',
          path.startsWith('decision.') ? 'is-guidance' : '',
          path === 'decision.soundhous_recommendation' ? 'is-recommendation' : ''
        ].filter(Boolean).join(' ');

        const valueCells = values.map((value) => (
          `<td class="shcmp-value">${escapeHtml(value ?? '—')}</td>`
        )).join('');

        return `
          <tr class="${rowClasses}" data-different="${isDifferent ? 'true' : 'false'}" data-shcmp-portable-row="true">
            <th scope="row" class="shcmp-label">${escapeHtml(label)}</th>
            ${valueCells}
          </tr>
        `;
      }).join('');

      if (!rowMarkup) return '';

      return `
        <tr class="shcmp-group-row${groupIndex === 0 ? ' shcmp-group-row--guidance' : ''}" data-shcmp-portable-row="true">
          <th colspan="${selection.length + 1}">${escapeHtml(group.label)}</th>
        </tr>
        ${rowMarkup}
      `;
    }).join('');

    if (markup) tbody.insertAdjacentHTML('beforeend', markup);
  };

  const bindRoot = (root) => {
    if (!root || root.dataset.shcmpPortableV2Ready === 'true') return;
    root.dataset.shcmpPortableV2Ready = 'true';

    let queued = false;
    const scheduleRender = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        renderPortableRows(root);
      });
    };

    const observer = new MutationObserver(scheduleRender);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-pressed']
    });

    root.querySelector('[data-shcmp-differences]')?.addEventListener('change', scheduleRender);
    root.querySelector('[data-shcmp-clear]')?.addEventListener('click', scheduleRender);
    root.querySelector('[data-shcmp-open]')?.addEventListener('click', scheduleRender);
    root.querySelector('[data-shcmp-close]')?.addEventListener('click', scheduleRender);
    root.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-shcmp-remove]')) scheduleRender();
    });

    scheduleRender();
  };

  const init = () => {
    document.querySelectorAll('[data-soundhous-compare-root]').forEach(bindRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
